import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { usePlatformBranding } from '../lib/branding'
// The route → title map lives in a pure module so non-React code (the Page
// Performance console) and Node tests can share it.
import { titleForPath } from '../lib/path-titles'

/**
 * Drives the browser tab title (`document.title`).
 *
 * Why this exists:
 *   The platform name was only ever consumed as the <Logo> alt text and
 *   in email previews, so changing it in Settings had no visible effect
 *   on the panel — the tab title stayed the hard-coded "TaskMagix" from
 *   index.html. This reads the name from `usePlatformBranding()`, which
 *   the Settings save mutation already invalidates (`['platform-branding']`),
 *   so saving a new Platform Name updates the tab header immediately with
 *   no page refresh.
 *
 * Why it is also route-aware (QA-091):
 *   Every route served the SAME title, so a person with several tabs open
 *   saw "TaskMagix" on all of them, browser history read as one repeated
 *   entry, and a bookmark carried no hint of what it pointed at. It is also
 *   the WCAG 2.4.2 "Page Titled" requirement: each page needs a title that
 *   describes its topic. The page name now leads, the platform name follows,
 *   which is the ordering that survives tab truncation.
 *
 * Renders nothing — mounted once at the app root.
 */

export function PlatformTitle() {
  const { data: branding } = usePlatformBranding()
  const { pathname } = useLocation()
  const name = branding?.name || 'TaskMagix'

  useEffect(() => {
    const page = titleForPath(pathname)
    document.title = page ? `${page} · ${name}` : name
  }, [name, pathname])

  return null
}
